import {
  citySelectOptions,
  type LocationCityId,
} from '@/collections/Locations/cityOptions'
import type {
  ApartmentAvailabilityItem,
  RoomTypeListingItem,
} from '@/lib/apartments/apartmentsQuery'
import {
  apartmentMatchesPreferenceId,
  furnishingToPreferenceId,
  roomTypesPreferenceIds,
  type RoomTypesPreferenceId,
} from '@/lib/apartments/apartmentListingFilterOptions'

export type { RoomTypesPreferenceId } from '@/lib/apartments/apartmentListingFilterOptions'

export type FilterCountOption<T extends string | number = string> = {
  count: number
  disabled?: boolean
  label: string
  value: T
}

export type LocationFilterChild = FilterCountOption<string> & {
  slug: string
}

export type LocationFilterGroup = {
  children: LocationFilterChild[]
  cityLabel: string
  citySlug: LocationCityId
  count: number
  /** Flat single-location cities use this as the selectable row value. */
  isGrouped: boolean
}

export type RoomTypesFilterState = {
  amenities: Set<RoomTypesPreferenceId>
  areaRange: [number, number]
  capacities: Set<number>
  locationSlugs: Set<string>
  /** Dual-range rent filter in €; defaults to full inventory priceBounds. */
  priceRange: [number, number]
  promotionsOnly: boolean
}

export type RoomTypesFilterOptions = {
  amenities: FilterCountOption<RoomTypesPreferenceId>[]
  areaBounds: [number, number]
  capacities: FilterCountOption<number>[]
  locations: LocationFilterGroup[]
  /** Stable inventory rent bounds from listing startingPrice / variant max prices. */
  priceBounds: [number, number]
  promotionsEligibleCount: number
}

export type RoomTypesFilterFacet =
  | 'promotions'
  | 'locations'
  | 'area'
  | 'price'
  | 'capacities'
  | 'amenities'

const CITY_ORDER = citySelectOptions.map((option) => option.value)

/** Fixed preference catalog — labels are overridden by i18n in the filter panel. */
export const ROOM_TYPES_PREFERENCE_OPTIONS: Array<{
  label: string
  value: RoomTypesPreferenceId
}> = [
  { value: 'fully-furnished-equipped', label: 'Fully Furnished & Equipped' },
  { value: 'fully-furnished', label: 'Fully furnished' },
  { value: 'accessibility', label: 'Accessibility' },
  { value: 'balcony', label: 'With balcony' },
  { value: 'patio', label: 'With patio' },
]

function apartmentMatchesPreference(
  apartment: ApartmentAvailabilityItem,
  preferenceId: RoomTypesPreferenceId,
): boolean {
  return apartmentMatchesPreferenceId(apartment.listingFilters, preferenceId)
}

/** Preference present on the listing or any sibling variant. */
export function listingHasPreference(
  listing: RoomTypeListingItem,
  preferenceId: RoomTypesPreferenceId,
): boolean {
  if (apartmentMatchesPreference(listing, preferenceId)) return true
  return listing.variants.some((variant) => apartmentMatchesPreference(variant, preferenceId))
}

/** Outdoor options present on any sibling variant in a room-type listing. */
export function getListingOutdoorOptions(
  listing: RoomTypeListingItem,
): Array<'balcony' | 'patio'> {
  const options: Array<'balcony' | 'patio'> = []

  if (listingHasPreference(listing, 'balcony')) {
    options.push('balcony')
  }

  if (listingHasPreference(listing, 'patio')) {
    options.push('patio')
  }

  return options
}

/** Furnishing preference for card display; prefers equipped over furnished when both exist. */
export function getListingFurnishingPreference(
  listing: RoomTypeListingItem,
): RoomTypesPreferenceId | null {
  if (listingHasPreference(listing, 'fully-furnished-equipped')) {
    return 'fully-furnished-equipped'
  }

  if (listingHasPreference(listing, 'fully-furnished')) {
    return 'fully-furnished'
  }

  return null
}

/** Room count shown on grouped cards when variants agree; otherwise uses the primary value. */
export function getListingDisplayRoomCount(listing: RoomTypeListingItem): number {
  const values = new Set(listing.variants.map((variant) => variant.roomCount))
  if (values.size === 1) {
    return [...values][0] ?? listing.roomCount
  }

  return listing.roomCount
}

/** Min rent = startingPrice; max rent = highest sibling variant price. */
export function getListingPriceBounds(listing: RoomTypeListingItem): [number, number] {
  const prices = listing.variants.map((variant) => variant.price)
  const minPrice = listing.startingPrice
  const maxPrice = prices.length > 0 ? Math.max(...prices, minPrice) : minPrice
  return [minPrice, maxPrice]
}

function listingHasCapacity(listing: RoomTypeListingItem, capacity: number): boolean {
  return listing.variants.some((variant) => variant.peopleCapacity === capacity)
}

function listingHasActivePromotion(
  listing: RoomTypeListingItem,
  promotedLocationSlugs: Set<string>,
): boolean {
  return promotedLocationSlugs.has(listing.locationSlug)
}

export function listingMatchesFilters(
  listing: RoomTypeListingItem,
  filters: RoomTypesFilterState,
  areaBounds: [number, number],
  priceBounds: [number, number],
  promotedLocationSlugs: Set<string>,
  excludeFacet: RoomTypesFilterFacet | null = null,
): boolean {
  const [areaMin, areaMax] = filters.areaRange
  const [areaBoundMin, areaBoundMax] = areaBounds
  const [priceMin, priceMax] = filters.priceRange
  const [priceBoundMin, priceBoundMax] = priceBounds
  const hasAreaFilter = areaMin > areaBoundMin || areaMax < areaBoundMax
  const hasPriceFilter = priceMin > priceBoundMin || priceMax < priceBoundMax
  const hasLocationFilter = filters.locationSlugs.size > 0
  const hasCapacityFilter = filters.capacities.size > 0
  const hasAmenityFilter = filters.amenities.size > 0
  const hasPromotionFilter = filters.promotionsOnly

  if (excludeFacet !== 'promotions' && hasPromotionFilter) {
    if (!listingHasActivePromotion(listing, promotedLocationSlugs)) {
      return false
    }
  }

  if (excludeFacet !== 'locations' && hasLocationFilter && !filters.locationSlugs.has(listing.locationSlug)) {
    return false
  }

  const listingSizeMax = listing.sizeMax ?? listing.size
  if (excludeFacet !== 'area' && hasAreaFilter && (listingSizeMax < areaMin || listing.size > areaMax)) {
    return false
  }

  const [listingPriceMin, listingPriceMax] = getListingPriceBounds(listing)
  if (
    excludeFacet !== 'price' &&
    hasPriceFilter &&
    (listingPriceMax < priceMin || listingPriceMin > priceMax)
  ) {
    return false
  }

  if (excludeFacet !== 'capacities' && hasCapacityFilter) {
    const matchesCapacity = Array.from(filters.capacities).some((capacity) =>
      listingHasCapacity(listing, capacity),
    )
    if (!matchesCapacity) return false
  }

  if (excludeFacet !== 'amenities' && hasAmenityFilter) {
    const matchesAmenity = Array.from(filters.amenities).some((preferenceId) =>
      listingHasPreference(listing, preferenceId),
    )
    if (!matchesAmenity) return false
  }

  return true
}

export function createDefaultFilterState(
  areaBounds: [number, number],
  priceBounds: [number, number] = [0, 0],
): RoomTypesFilterState {
  return {
    amenities: new Set(),
    areaRange: [...areaBounds] as [number, number],
    capacities: new Set(),
    locationSlugs: new Set(),
    priceRange: [...priceBounds] as [number, number],
    promotionsOnly: false,
  }
}

export function deriveGlobalAreaBounds(listings: RoomTypeListingItem[]): [number, number] {
  if (listings.length === 0) return [0, 0]

  let minSize = Number.POSITIVE_INFINITY
  let maxSize = Number.NEGATIVE_INFINITY

  for (const listing of listings) {
    const listingSizeMax = listing.sizeMax ?? listing.size
    minSize = Math.min(minSize, listing.size)
    maxSize = Math.max(maxSize, listingSizeMax)
  }

  return [Math.floor(minSize), Math.ceil(maxSize)]
}

/**
 * Stable price slider bounds from the full inventory.
 * Uses each listing's startingPrice (min) and highest sibling variant price (max).
 */
export function deriveGlobalPriceBounds(listings: RoomTypeListingItem[]): [number, number] {
  if (listings.length === 0) return [0, 0]

  let minPrice = Number.POSITIVE_INFINITY
  let maxPrice = Number.NEGATIVE_INFINITY

  for (const listing of listings) {
    const [listingMin, listingMax] = getListingPriceBounds(listing)
    minPrice = Math.min(minPrice, listingMin)
    maxPrice = Math.max(maxPrice, listingMax)
  }

  return [Math.floor(minPrice), Math.ceil(maxPrice)]
}

function filterListingsForFacet(
  listings: RoomTypeListingItem[],
  filters: RoomTypesFilterState,
  areaBounds: [number, number],
  priceBounds: [number, number],
  promotedLocationSlugs: Set<string>,
  excludeFacet: RoomTypesFilterFacet,
): RoomTypeListingItem[] {
  return listings.filter((listing) =>
    listingMatchesFilters(
      listing,
      filters,
      areaBounds,
      priceBounds,
      promotedLocationSlugs,
      excludeFacet,
    ),
  )
}

function countListingsByLocation(listings: RoomTypeListingItem[]): Map<string, number> {
  const counts = new Map<string, number>()

  for (const listing of listings) {
    counts.set(listing.locationSlug, (counts.get(listing.locationSlug) ?? 0) + 1)
  }

  return counts
}

function countListingsByCapacity(listings: RoomTypeListingItem[]): Map<number, number> {
  const counts = new Map<number, number>()

  for (const listing of listings) {
    const capacities = new Set(listing.variants.map((variant) => variant.peopleCapacity))
    for (const capacity of capacities) {
      counts.set(capacity, (counts.get(capacity) ?? 0) + 1)
    }
  }

  return counts
}


function countListingsByPreference(listings: RoomTypeListingItem[]): Map<RoomTypesPreferenceId, number> {
  const counts = new Map<RoomTypesPreferenceId, number>()

  for (const preferenceId of roomTypesPreferenceIds) {
    counts.set(
      preferenceId,
      listings.filter((listing) => listingHasPreference(listing, preferenceId)).length,
    )
  }

  return counts
}

type InventoryLocation = {
  cityLabel: string
  citySlug: LocationCityId
  label: string
  slug: string
}

function collectInventoryLocations(listings: RoomTypeListingItem[]): InventoryLocation[] {
  const bySlug = new Map<string, InventoryLocation>()

  for (const listing of listings) {
    if (bySlug.has(listing.locationSlug)) continue

    bySlug.set(listing.locationSlug, {
      cityLabel: listing.locationCityTitle,
      citySlug: listing.locationCitySlug,
      label: listing.locationTitle,
      slug: listing.locationSlug,
    })
  }

  return [...bySlug.values()].sort((left, right) => {
    const cityCompare = left.cityLabel.localeCompare(right.cityLabel)
    if (cityCompare !== 0) return cityCompare
    return left.label.localeCompare(right.label)
  })
}

function buildLocationGroups(
  listings: RoomTypeListingItem[],
  locationCounts: Map<string, number>,
  selectedLocationSlugs: Set<string>,
): LocationFilterGroup[] {
  const inventoryLocations = collectInventoryLocations(listings)
  const locationsByCity = new Map<LocationCityId, LocationFilterChild[]>()
  const cityLabels = new Map<LocationCityId, string>()

  for (const location of inventoryLocations) {
    const count = locationCounts.get(location.slug) ?? 0
    const children = locationsByCity.get(location.citySlug) ?? []
    children.push({
      count,
      disabled: count === 0 && !selectedLocationSlugs.has(location.slug),
      label: location.label,
      slug: location.slug,
      value: location.slug,
    })
    locationsByCity.set(location.citySlug, children)
    cityLabels.set(location.citySlug, location.cityLabel)
  }

  return CITY_ORDER.flatMap((citySlug) => {
    const children = locationsByCity.get(citySlug)
    if (!children || children.length === 0) return []

    return [
      {
        children,
        cityLabel: cityLabels.get(citySlug) ?? citySlug,
        citySlug,
        count: children.reduce((sum, child) => sum + child.count, 0),
        isGrouped: children.length > 1,
      },
    ]
  })
}

function collectGlobalCapacities(listings: RoomTypeListingItem[]): number[] {
  const values = new Set<number>()

  for (const listing of listings) {
    for (const variant of listing.variants) {
      values.add(variant.peopleCapacity)
    }
  }

  return Array.from(values).sort((left, right) => left - right)
}

function collectGlobalPreferences(listings: RoomTypeListingItem[]): RoomTypesPreferenceId[] {
  return roomTypesPreferenceIds.filter((preferenceId) =>
    listings.some((listing) => listingHasPreference(listing, preferenceId)),
  )
}

/** @deprecated Use deriveContextualRoomTypesFilterOptions for interactive filtering. */
export function deriveRoomTypesFilterOptions(
  listings: RoomTypeListingItem[],
): RoomTypesFilterOptions {
  const areaBounds = deriveGlobalAreaBounds(listings)
  const priceBounds = deriveGlobalPriceBounds(listings)
  return deriveContextualRoomTypesFilterOptions(
    listings,
    createDefaultFilterState(areaBounds, priceBounds),
    new Set<string>(),
  )
}

export function deriveContextualRoomTypesFilterOptions(
  listings: RoomTypeListingItem[],
  filters: RoomTypesFilterState,
  promotedLocationSlugs: Set<string>,
): RoomTypesFilterOptions {
  const areaBounds = deriveGlobalAreaBounds(listings)
  const priceBounds = deriveGlobalPriceBounds(listings)
  const globalCapacities = collectGlobalCapacities(listings)
  const globalPreferences = collectGlobalPreferences(listings)

  const locationListings = filterListingsForFacet(
    listings,
    filters,
    areaBounds,
    priceBounds,
    promotedLocationSlugs,
    'locations',
  )
  const capacityListings = filterListingsForFacet(
    listings,
    filters,
    areaBounds,
    priceBounds,
    promotedLocationSlugs,
    'capacities',
  )
  const amenityListings = filterListingsForFacet(
    listings,
    filters,
    areaBounds,
    priceBounds,
    promotedLocationSlugs,
    'amenities',
  )
  const promotionListings = filterListingsForFacet(
    listings,
    filters,
    areaBounds,
    priceBounds,
    promotedLocationSlugs,
    'promotions',
  )

  const locationCounts = countListingsByLocation(locationListings)
  const capacityCounts = countListingsByCapacity(capacityListings)
  const preferenceCounts = countListingsByPreference(amenityListings)

  const promotionsEligibleCount = promotionListings.filter((listing) =>
    listingHasActivePromotion(listing, promotedLocationSlugs),
  ).length

  const locations = buildLocationGroups(listings, locationCounts, filters.locationSlugs)

  const capacities =
    listings.length === 0
      ? []
      : globalCapacities.map((value) => {
          const count = capacityCounts.get(value) ?? 0
          return {
            count,
            disabled: count === 0 && !filters.capacities.has(value),
            label: String(value),
            value,
          }
        })

  const amenities =
    listings.length === 0
      ? []
      : globalPreferences.map((value) => {
          const preference = ROOM_TYPES_PREFERENCE_OPTIONS.find((option) => option.value === value)
          const count = preferenceCounts.get(value) ?? 0
          return {
            count,
            disabled: count === 0 && !filters.amenities.has(value),
            label: preference?.label ?? value,
            value,
          }
        })

  return {
    amenities,
    areaBounds,
    capacities,
    locations,
    priceBounds,
    promotionsEligibleCount,
  }
}

export function filterRoomTypeListings(
  listings: RoomTypeListingItem[],
  filters: RoomTypesFilterState,
  areaBounds: [number, number],
  priceBounds: [number, number],
  promotedLocationSlugs: Set<string> = new Set<string>(),
): RoomTypeListingItem[] {
  return listings.filter((listing) =>
    listingMatchesFilters(listing, filters, areaBounds, priceBounds, promotedLocationSlugs),
  )
}

/** @deprecated Use filterRoomTypeListings for grouped room-type cards. */
export function filterApartments(
  apartments: ApartmentAvailabilityItem[],
  filters: RoomTypesFilterState,
  areaBounds: [number, number],
  priceBounds: [number, number] = [0, 0],
): ApartmentAvailabilityItem[] {
  const [areaMin, areaMax] = filters.areaRange
  const [areaBoundMin, areaBoundMax] = areaBounds
  const [priceMin, priceMax] = filters.priceRange
  const [priceBoundMin, priceBoundMax] = priceBounds
  const hasAreaFilter = areaMin > areaBoundMin || areaMax < areaBoundMax
  const hasPriceFilter = priceMin > priceBoundMin || priceMax < priceBoundMax
  const hasLocationFilter = filters.locationSlugs.size > 0
  const hasCapacityFilter = filters.capacities.size > 0
  const hasAmenityFilter = filters.amenities.size > 0

  return apartments.filter((apartment) => {
    if (hasLocationFilter && !filters.locationSlugs.has(apartment.locationSlug)) {
      return false
    }

    if (hasAreaFilter && (apartment.size < areaMin || apartment.size > areaMax)) {
      return false
    }

    if (hasPriceFilter && (apartment.price < priceMin || apartment.price > priceMax)) {
      return false
    }

    if (hasCapacityFilter && !filters.capacities.has(apartment.peopleCapacity)) {
      return false
    }

    if (hasAmenityFilter) {
      const hasMatchingAmenity = Array.from(filters.amenities).some((preferenceId) =>
        apartmentMatchesPreference(apartment, preferenceId),
      )
      if (!hasMatchingAmenity) return false
    }

    return true
  })
}

export function toggleSetValue<T>(set: Set<T>, value: T, checked: boolean): Set<T> {
  const next = new Set(set)
  if (checked) {
    next.add(value)
  } else {
    next.delete(value)
  }
  return next
}

// Re-export for card copy helpers that map furnishing enum to preference ids.
export { furnishingToPreferenceId }
